import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  Modal,
  TextBody,
  Button,
  get,
  Box,
  TextHeading,
  useModal,
  Link,
  Table,
  TableColumn,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { normalizeBalanceLimits } from '../../../../utils/normalizeBalanceLimits'
import { InterestRate } from '../../../../types'

dayjs.extend(timezone)
dayjs.extend(utc)

interface InterestRateTableRow {
  cell: {
    value: number
    row: {
      original: InterestRate
    }
  }
}

const StyledBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${get.space('md')};
  margin-bottom: ${get.space('xl')}; ;
`

const StyledLink = styled(Link)`
  font-size: ${get.fontSizes('md')};
`

const StyledButton = styled(Button)`
  font-size: ${get.fontSizes('md')};
`

const StyledTextHeading = styled(TextHeading)`
  margin-bottom: ${get.space('xs')};
`

const columns: TableColumn<InterestRate>[] = [
  {
    Header: 'Balance Requirement',
    accessor: 'higherAmountLimit',
    alignment: 'left',
    Cell: ({
      cell: {
        value,
        row: {
          original: { lowerAmountLimit },
        },
      },
    }: InterestRateTableRow): string => {
      return normalizeBalanceLimits(lowerAmountLimit, value)
    },
  },
  {
    Header: 'APY',
    accessor: 'apy',
    alignment: 'right',
    Cell: ({ cell: { value } }: { cell: { value: number } }): string =>
      `${value}%`,
  },
  {
    Header: 'Interest Rate',
    accessor: 'rate',
    alignment: 'right',
    Cell: ({ cell: { value } }: { cell: { value: number } }): string =>
      `${value}%`,
  },
]

const headerRef = React.createRef<HTMLHeadingElement>()

const ModalTrigger: React.FC = () => {
  const { showModal } = useModal()
  return (
    <StyledButton
      onClick={showModal}
      data-testid="view-rates-button"
      variant="link"
      allytmln="viewRates"
      text="View rates"
    />
  )
}

const ModalContent: React.FC<{
  interestRates: InterestRate[]
}> = ({ interestRates }) => {
  const today = dayjs().tz('America/New_York').format('MM/DD/YYYY')
  return (
    <Box m="md">
      <StyledBox>
        <TextHeading
          tag="h1"
          size="md"
          tabIndex={-1}
          data-testId="view-rates-modal-title"
          ref={headerRef}
        >
          You haven&apos;t funded your account yet
        </TextHeading>
        <TextBody tag="p" size="md">
          Start earning interest by funding your account. On the date you fund
          your CD, you will receive the current rate we offer for your term and
          balance tier.
        </TextBody>
        <TextBody tag="p" size="md">
          Rates and Annual Percentage Yield (APY) are accurate as of {today}.
        </TextBody>
        <Box>
          <StyledTextHeading tag="h4" size="xs">
            Today&apos;s CD rates
          </StyledTextHeading>
          <Table
            config={{ data: interestRates, columns }}
            shared={{ captionText: "Today's CD rates" }}
            table={{ fullWidth: true }}
          />
        </Box>

        <StyledLink text="Make a Deposit" to="/payments/deposit-checks" />
        <StyledLink text="Start a Transfer" to="/payments/transfers" />
      </StyledBox>
    </Box>
  )
}

export const ViewRatesModal: React.FC<{
  interestRates: InterestRate[]
}> = ({ interestRates }) => {
  return (
    <Modal
      ref={headerRef}
      trigger={<ModalTrigger />}
      content={<ModalContent interestRates={interestRates} />}
      width="lg"
    />
  )
}
